import '@babel/polyfill';
import uiRouter from '@uirouter/angularjs';
import '@uirouter/angularjs/release/stateEvents';
import 'libs/angular-material-custom/angular-material';
import 'libs/angular-material-custom/angular-material.css';
import ngTranslate from 'angular-translate';
import 'angular-translate-loader-static-files';
import 'angular-translate-loader-url';
import ngAnimate from 'angular-animate';
import ngAria from 'angular-aria';
import ngMessages from 'angular-messages';
import ngCookies from 'angular-cookies';
import ngSanitize from 'angular-sanitize';
import ngCreditCards from 'angular-credit-cards';
import 'cloudinary-angular';
import ravenJs from 'raven-js';
import ngRaven from 'raven-js/plugins/angular';
import { polyfill as mobileDragDropPolyfill } from 'mobile-drag-drop';

import { ApiInterceptorModule } from 'services/interceptors/api.interceptor';
import { AppSettingsModule } from 'services/settings.provider';
import { TitleCaseFilterModule } from 'filters/titleCase.filter';
import { GtmClickEventDirectiveModule } from 'directives/gtmClickEvent.directive';
import { AnalyticsClickEventDirectiveModule } from 'directives/analyticsClickEvent.directive';
import { IeSmoothScrollModule } from 'directives/ieSmoothScroll.directive';
import { BrowserClassesModule } from 'directives/browserClasses.directive';
import { AuthServiceModule } from 'services/auth/auth.service';
import { Auth0SdkServiceModule } from 'services/auth/auth0sdk.service';
import { Auth0AuthServiceModule } from 'services/auth/auth0auth.service';
import { NativePlatformServiceModule } from 'services/access/nativePlatform/nativePlatform.service';
import { MdMenuCleanerServiceModule } from 'services/utilities/mdMenuCleaner.service';
import { LaunchAppExtensionPreviewServiceModule } from
    'components/appExtensionPreview/launchAppExtensionPreview.service';
import { OptimizelyServiceModule } from 'services/data/optimizely.service';

import { AppComponentModule } from './app.component';
import { AppServiceModule } from './app.service';
import { PagesModule } from './pages/pages.module';
import { AppLabelsModule } from './labels';
import { RedirectsModule } from './redirects';

import appCardConstants from './pages/appStore2/components/appCardConstants';
import { AUTH0_PASSWORD_EXPIRED_CLAIM, LOGIN_REDIRECT_COOKIE } from './constants';

mobileDragDropPolyfill();

ravenJs
    .config('https://c25632fa0e9c45129c3c19e579235aa7@app.getsentry.com/81463', {
        release: WEBPACK_GITHUB_VERSION,
        environment: WEBPACK_ENV,
        ignoreUrls: [
            /localhost:\d+\//i,
            /127\.0\.0\.1:\d+\//i,
            // Chrome extensions
            /extensions\//i,
            /^chrome:\/\//i
        ]
    })
    .addPlugin(ngRaven, angular)
    .install();

export const AppModule = angular
    .module('app', [
        uiRouter,
        'ui.router.state.events',
        ngTranslate,
        'ngMaterial',

        ngAnimate,
        ngAria,
        ngMessages,
        ngSanitize,
        ngCookies,
        ngCreditCards,
        'cloudinary',
        ngRaven.moduleName,

        AppLabelsModule,
        PagesModule,

        ApiInterceptorModule,
        AppSettingsModule,
        TitleCaseFilterModule,
        GtmClickEventDirectiveModule,
        AnalyticsClickEventDirectiveModule,
        IeSmoothScrollModule,
        BrowserClassesModule,
        AuthServiceModule,
        Auth0SdkServiceModule,
        Auth0AuthServiceModule,
        NativePlatformServiceModule,
        MdMenuCleanerServiceModule,
        AppComponentModule,
        AppServiceModule,
        LaunchAppExtensionPreviewServiceModule,
        OptimizelyServiceModule,
        RedirectsModule
    ])
    .config(($provide, $httpProvider) => {
        $httpProvider.interceptors.push('apiInterceptor');
    })
    .config(($sceDelegateProvider) => {
        $sceDelegateProvider.resourceUrlWhitelist([
            '**'
        ]);
    })
    .config(($locationProvider, $urlRouterProvider, $stateProvider) => {
        // We defer routing initialization until we've registered redirects to the new admin.
        // This is due to the integration with launch darkly to control the redirects at a user
        // level, which requires a network call.
        $urlRouterProvider.deferIntercept();
        $locationProvider.html5Mode(true);
        $urlRouterProvider.when('/', (
            $match,
            $state,
            $cookies,
            $window,
            auth0AuthService
        ) => {
            'ngInject';

            const handleAuthenticated = () => {
                auth0AuthService.getUser().then((user) => {
                    const redirect = $cookies.get(LOGIN_REDIRECT_COOKIE);
                    $cookies.remove(LOGIN_REDIRECT_COOKIE);

                    if (user[AUTH0_PASSWORD_EXPIRED_CLAIM]) {
                        $state.transitionTo('myAccount', $match, false);
                    } else if (redirect && redirect.length > 1 && redirect.charAt(0) === '/') {
                        $window.location.href = process.env.BASE_ADMIN_URL + redirect;
                    } else {
                        $state.transitionTo('dashboard', $match, false);
                    }
                });
            };

            const checkForAuthentications = (checkForToken) => {
                return auth0AuthService.isAuthenticated().then((authenticated) => {
                    if (authenticated) {
                        handleAuthenticated();
                    } else if (checkForToken) {
                        auth0AuthService.getTokenSilently().then(() => {
                            return checkForAuthentications(false);
                        }).catch(() => {
                            auth0AuthService.unauthenticatedHandler($window.location.search, $match);
                        });
                    } else {
                        auth0AuthService.unauthenticatedHandler($window.location.search, $match);
                    }
                });
            };

            auth0AuthService.clearLegacyUserCookies();
            checkForAuthentications(true).catch(() => {
                auth0AuthService.unauthenticatedHandler($window.location.search, $match);
            });
        });

        $urlRouterProvider.otherwise('/dashboard');

        $stateProvider.state('logout', {
            url: '/logout',
            controller: (auth0AuthService) => {
                'ngInject';

                auth0AuthService.logout();
            }
        });
    })
    .config(($translateProvider, appLabels) => {
        $translateProvider.translations('en', appLabels);
        $translateProvider.useSanitizeValueStrategy(null);
        $translateProvider
            .preferredLanguage('en')
            .fallbackLanguage('en');
    })
    .config(($mdThemingProvider) => {
        let vlnAmber = $mdThemingProvider.extendPalette('amber', {
            contrastLightColors: ['A700']
        });
        $mdThemingProvider.definePalette('vln-secondary', vlnAmber);

        let vlnWarn = $mdThemingProvider.extendPalette('grey', {
            500: '#FFF',
            A700: '#FFF'
        });
        $mdThemingProvider.definePalette('vln-warn', vlnWarn);

        $mdThemingProvider.theme('default')
            .primaryPalette('grey')
            .accentPalette('vln-secondary', {
                default: 'A700'
            });

        $mdThemingProvider.theme('delete-confirm')
            .primaryPalette('grey');

        $mdThemingProvider.theme('dark')
            .accentPalette('vln-warn')
            .warnPalette('vln-warn')
            .dark();
    })
    .config(($provide, $compileProvider) => {
        $compileProvider.commentDirectivesEnabled(false);

        if (WEBPACK_ENV === 'production') {
            $compileProvider.debugInfoEnabled(false);
        }

        $provide.decorator('$window', ($delegate) => {
            let location = $delegate.location;
            // IE doesn't support window.location.origin
            if (!location.origin) {
                location.origin =
                    `${location.protocol}//${location.hostname}${location.port ? `:${location.port}` : ''}`;
            }
            return $delegate;
        });
    })
    .config((CloudinaryProvider) => {
        CloudinaryProvider.configure({
            cloud_name: process.env.CLOUDINARY_NAME
        });
    })
    .run(($rootScope, $window, $transitions, $translate, $mdDialog, $state, mdMenuCleaner, $filter, $location,
        appsService, launchAppExtensionModalService, optimizelyService, auth0AuthService, $stateParams) => {
        var $body = angular.element($window.document.body);
        let pageClass;

        $rootScope.$on('$locationChangeSuccess', (e, toUrl) => {
            auth0AuthService.isAuthenticated().then((authenticated) => {
                const url = new URL(toUrl);
                if (!authenticated && url.pathname !== '/login') {
                    return auth0AuthService.getTokenSilently();
                }
                return false;
            }).then((token) => {
                if (token) {
                    return !auth0AuthService.isAuthenticated();
                }
                return false;
            }).then((shouldHandle) => {
                if (shouldHandle) {
                    return auth0AuthService.unauthenticatedHandler($window.location.search);
                }
            });
        });

        // add page-controllerName class to body on route changes
        $rootScope.$on('$stateChangeSuccess', (e, toState, toParams, fromState) => {
            if (toState) {
                $body.removeClass(pageClass);
                let pageName = toState.data && toState.data.page ? toState.data.page : toState.name;
                pageClass = `page--${pageName}`;
                $body.addClass(pageClass);

                if (fromState.name === 'dashboard') {
                    $body.addClass('page--from-dashboard');
                } else {
                    $body.removeClass('page--from-dashboard');
                }
            }

            const sku = $location.search().installApp || $stateParams.installApp;
            if (sku) {
                appsService.getApps().then((apps) => {
                    const filterApps = apps.filter((app) => {
                        return app.sku === sku;
                    });
                    return filterApps[0];
                }).then((app) => {
                    if (app) {
                        return launchAppExtensionModalService.launchPreview('application', app, app.enabled)
                            .then((appResult) => {
                                let link = 'dashboard';
                                if (appResult.event === 'apps:newInstalled') {
                                    let appInfo = appCardConstants.APPS.filter(apps => apps.sku === appResult.sku);
                                    link = appInfo.length ? appInfo[0].link : 'dashboard';
                                }
                                $state.go(link);
                            });
                    }
                });
            }

            //Hint: Check service's definition for docs on why we need this.
            mdMenuCleaner.clean();

            $mdDialog.cancel();

            optimizelyService.getExperiments();
        });

        $rootScope.$on('$viewContentLoaded', () => {
            angular.element($window).scrollTop(0);
            angular.element('body.safari-mobile .layout__scope').scrollTop(0);
        });

        $transitions.onSuccess({}, (transition) => {
            let fromState = transition.$from();
            let toState = transition.$to();

            if ($window.dataLayer && fromState.name) {
                $window.dataLayer.push({
                    event: 'virtualPageView',
                    virtualPage: $location.path(),
                    virtualTitle: toState.name
                });
            }

            if (toState.data && toState.data.title) {
                $translate(toState.data.title).then((data) => {
                    $rootScope.title = $filter('titleCase')(data || toState.name);
                });
            } else if (toState && toState.data && toState.data.rawTitle) {
                $rootScope.rawTitle = toState.data.rawTitle;
            } else {
                $rootScope.title = '';
            }
        });

        $rootScope.$on('$stateChangeError', (event, toState, toParams, fromState, fromParams, error) => {
            if (error.detail && error.detail.redirectTo && error.detail.redirectTo === fromState.name) {
                $state.reload().then(errorAlert);
                return;
            }
            if (error.detail && error.detail.redirectTo) {
                $state.go(error.detail.redirectTo, error.detail.params).then(errorAlert);
                return;
            }
            if (toState.name !== 'dashboard') {
                $state.go('dashboard', null, { reload: true }).then(() => {
                    showAlert({ title: 'Error loading page', body: 'Please try again later' });
                });
            } else {
                $state.go('error');
            }

            function errorAlert() {
                if (error.detail.errorMessage) {
                    showAlert(error.detail.errorMessage);
                }
            }
            function showAlert(errorMessage) {
                const { title, body } = errorMessage;
                const errorOk = 'Ok';
                return $mdDialog.show(
                    $mdDialog.alert()
                        .title(title)
                        .htmlContent(body)
                        .ariaLabel(title)
                        .ok(errorOk)
                );
            }
        });
    })
    .run((redirectsService, $urlRouter) => {
        // Register user redirects to new admin, then re-enable routing.
        redirectsService.registerRedirects().then(() => {
            $urlRouter.sync();
            $urlRouter.listen();
        });
    })
    .run(($window, authTokenService) => {
        authTokenService.getTenantId().then((tenant) => {
            if ($window.Raven) {
                $window.Raven.setUserContext({
                    id: tenant
                });
            }
        });
    })
    .name;



// WEBPACK FOOTER //
// ./src/app/app.module.js